import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";
import {
  History, Loader2, User, Lock,
  DollarSign, MapPin, FileText, Package, Scale, Hash, Pencil, Ship, Globe, RefreshCw
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";

/* ── Friendly-name dictionary ── */
const FIELD_LABELS: Record<string, { label: string; icon: typeof DollarSign; category: "money" | "text" | "logistics" | "item" }> = {
  seguro:          { label: "Costo de Seguro",              icon: DollarSign, category: "money" },
  costo_flete:     { label: "Flete Internacional",          icon: Ship,       category: "money" },
  total_factura:   { label: "Valor Total Factura",          icon: DollarSign, category: "money" },
  impuestos:       { label: "Impuestos",                    icon: DollarSign, category: "money" },
  otros_cargos:    { label: "Otros Cargos",                 icon: DollarSign, category: "money" },
  descuento_ajuste:{ label: "Descuento / Ajuste",           icon: DollarSign, category: "money" },
  precio_unitario: { label: "Precio Unitario",              icon: DollarSign, category: "money" },
  sub_total:       { label: "Subtotal Línea",               icon: DollarSign, category: "money" },
  hs_code:         { label: "Partida Arancelaria (HS Code)",icon: Hash,       category: "item" },
  sku:             { label: "SKU",                          icon: Package,    category: "item" },
  cantidad:        { label: "Cantidad",                     icon: Package,    category: "item" },
  descripcion:     { label: "Descripción del Ítem",         icon: FileText,   category: "item" },
  peso_bruto_kg:   { label: "Peso Bruto (kg)",              icon: Scale,      category: "item" },
  peso_neto_kg:    { label: "Peso Neto (kg)",               icon: Scale,      category: "item" },
  volumen_cbm:     { label: "Volumen (CBM)",                icon: Scale,      category: "logistics" },
  incoterm:        { label: "Incoterm",                     icon: Globe,      category: "logistics" },
  origen:          { label: "País de Origen",               icon: MapPin,     category: "logistics" },
  destino:         { label: "Destino",                      icon: MapPin,     category: "logistics" },
  emisor:          { label: "Emisor",                       icon: FileText,   category: "text" },
  receptor:        { label: "Receptor",                     icon: FileText,   category: "text" },
  moneda:          { label: "Moneda",                       icon: DollarSign, category: "money" },
  no_documento:    { label: "N.º Documento",                icon: FileText,   category: "text" },
  fecha:           { label: "Fecha del Documento",          icon: FileText,   category: "text" },
  notas_tecnicas:  { label: "Notas Técnicas",              icon: Pencil,     category: "text" },
  nota_item:       { label: "Nota del Ítem",               icon: Pencil,     category: "text" },
  estado:          { label: "Estado",                       icon: FileText,   category: "text" },
  tipo_documento:  { label: "Tipo de Documento",            icon: FileText,   category: "text" },
  total_piezas:    { label: "Total de Piezas",              icon: Package,    category: "item" },
  unidad_medida:   { label: "Unidad de Medida",             icon: Scale,      category: "item" },
  iva_override:    { label: "IVA Override (%)",              icon: DollarSign, category: "money" },
  precio_venta_esperado: { label: "Precio de Venta Sugerido", icon: DollarSign, category: "money" },
  item_no:         { label: "Número de Ítem",               icon: Hash,       category: "item" },
  metodo_pago:     { label: "Método de Pago",               icon: DollarSign, category: "text" },

  /* ── TSK-210: Auditoría granular de gastos del embarque ── */
  "gasto.concepto":         { label: "Gasto · Concepto",            icon: FileText,   category: "text" },
  "gasto.monto":            { label: "Gasto · Monto",               icon: DollarSign, category: "money" },
  "gasto.moneda":           { label: "Gasto · Moneda",              icon: DollarSign, category: "money" },
  "gasto.trm_pago":         { label: "Gasto · TRM de Pago Manual",  icon: RefreshCw,  category: "money" },
  "gasto.base_prorrateo":   { label: "Gasto · Base de Prorrateo",   icon: Scale,      category: "logistics" },
  "gasto.aplica_costeo":    { label: "Gasto · Aplica al Costeo",    icon: DollarSign, category: "money" },
  "gasto.notas":            { label: "Gasto · Notas",               icon: Pencil,     category: "text" },
};

const DEFAULT_META = { label: "", icon: Pencil, category: "text" as const };

function getMeta(field: string) {
  // TSK-210: Strip namespace prefix for fallback rendering (e.g. "gasto.foo" -> "Foo")
  const m = FIELD_LABELS[field];
  if (m) return m;
  const clean = field.includes(".") ? field.split(".").slice(1).join(".") : field;
  return { ...DEFAULT_META, label: clean.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) };
}

function isMoney(cat: string) {
  return cat === "money";
}

function formatDisplay(val: string | null, money: boolean) {
  if (!val || val === "—") return "—";
  if (money) {
    const n = Number(val);
    if (!isNaN(n)) return `$${n.toLocaleString("es-CO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return val;
}

/* ── Types ── */
interface AuditEntry {
  id: string;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  created_at: string;
  user_id: string | null;
  record_hash: string | null;
  email?: string;
}

interface Props {
  documentId: string;
  className?: string;
}

export function AuditTrailTimeline({ documentId, className }: Props) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const { orgId } = useAuth();

  useEffect(() => {
    if (!documentId) return;
    setLoading(true);

    (async () => {
      const { data: logs, error } = await supabase
        .from("audit_logs")
        .select("*")
        .eq("document_id", documentId)
        .order("created_at", { ascending: false });

      if (error || !logs) {
        console.error("Error fetching audit logs:", error?.message);
        setEntries([]);
        setLoading(false);
        return;
      }

      const userIds = [...new Set(logs.map(l => l.user_id).filter(Boolean))] as string[];
      let emailMap: Record<string, string> = {};

      if (orgId && userIds.length > 0) {
        const { data: members } = await supabase.rpc("get_org_members_with_email", { _org_id: orgId });
        if (members) {
          for (const m of members) emailMap[m.user_id] = m.email;
        }
      }

      setEntries(logs.map(l => ({ ...l, email: l.user_id ? emailMap[l.user_id] : undefined })));
      setLoading(false);
    })();
  }, [documentId, orgId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
        <History className="h-8 w-8 opacity-30" />
        <p className="text-sm">No hay cambios registrados</p>
      </div>
    );
  }

  return (
    <ScrollArea className={cn("max-h-[500px]", className)}>
      <div className="relative pl-6 pr-2">
        {/* Vertical line */}
        <div className="absolute left-[11px] top-2 bottom-2 w-px bg-border" />

        <div className="space-y-0">
          {entries.map((entry, idx) => {
            const isReplace = entry.field_name === "document_replace";
            const meta = getMeta(entry.field_name);
            const Icon = isReplace ? RefreshCw : meta.icon;
            const money = !isReplace && isMoney(meta.category);
            const oldDisplay = formatDisplay(entry.old_value, money);
            const newDisplay = formatDisplay(entry.new_value, money);
            const userName = entry.email?.split("@")[0] ?? "Usuario desconocido";

            return (
              <div key={entry.id} className="relative flex items-start gap-3 py-3">
                {/* Dot */}
                <div className={cn(
                  "absolute left-[-17px] top-[18px] h-2.5 w-2.5 rounded-full border-2 border-background",
                  isReplace ? "bg-amber-500" : idx === 0 ? "bg-primary" : "bg-muted-foreground/40"
                )} />

                <div className="flex-1 min-w-0">
                  {/* Header: user + time */}
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                    <User className="h-3 w-3 shrink-0" />
                    <span className="font-medium text-foreground truncate">{userName}</span>
                    <span>·</span>
                    <span className="shrink-0">
                      {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true, locale: es })}
                    </span>
                    {entry.record_hash && (
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Lock className="h-3 w-3 text-emerald-500 shrink-0 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs font-mono text-[10px] break-all">
                            <p className="font-sans text-xs font-semibold mb-1">Hash SHA-256</p>
                            {entry.record_hash}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>

                  {/* Body */}
                  {isReplace ? (
                    <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2.5">
                      <p className="text-sm leading-relaxed flex flex-wrap items-center gap-1.5">
                        <RefreshCw className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
                        <Badge variant="outline" className="border-amber-500/40 bg-amber-500/20 text-amber-700 dark:text-amber-300 text-[11px] font-semibold">
                          Sustitución
                        </Badge>
                        <span className="text-foreground">{entry.new_value}</span>
                      </p>
                      {entry.old_value && (
                        <p className="mt-1.5 text-xs text-muted-foreground font-mono">
                          ID anterior: <span className="select-all text-foreground/70">{entry.old_value}</span>
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm leading-relaxed flex flex-wrap items-center gap-1.5">
                      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span>Actualizó</span>
                      <span className="font-semibold text-foreground">{meta.label}</span>
                      <span>de</span>
                      <Badge
                        variant="outline"
                        className="font-mono text-[11px] border-destructive/30 bg-destructive/10 text-destructive line-through"
                      >
                        {oldDisplay}
                      </Badge>
                      <span>a</span>
                      <Badge
                        variant="outline"
                        className="font-mono text-[11px] border-primary/30 bg-primary/10 text-primary font-bold"
                      >
                        {newDisplay}
                      </Badge>
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </ScrollArea>
  );
}
